import React, { useState } from "react";
import { Link } from "../router";
import PageHero from "../components/PageHero";

const VALUES = [
  ["Wealth-first thinking", "Every desk here is measured on the value it creates for clients — monetization, ROI, and disciplined capital deployment, not vanity volume."],
  ["Digital-first workflows", "Listings, vetting, EMI routing and deal flow run on tooling we build ourselves. You'll work fast, with leverage, not paperwork."],
  ["Verified over volume", "We'd rather list ten clean assets than a thousand questionable ones. That standard applies to how we hire, too."],
];

export default function CareersPage() {
  const [open, setOpen] = useState(true);

  return (
    <>
      <PageHero
        eyebrow="Careers · Aditi Realty"
        title="Build the house that"
        italic="raised the bar."
        sub="We're a small, high-performance team rebuilding how serious property gets bought, financed and built in India. If you think in assets, returns and craft — you'll fit."
        tone={["#0B0B0C", "#151c19"]}
      />

      {/* Culture */}
      <section className="section">
        <div className="shell">
          <div className="reveal" style={{ maxWidth: 640, marginBottom: 50 }}>
            <span className="eyebrow">Our culture</span>
            <h2 className="h-xl" style={{ marginTop: 16 }}>High performance,<br />quietly delivered.</h2>
            <p className="lead" style={{ marginTop: 18 }}>
              No noise, no churn-and-burn brokerage floor. We hire few, trust deeply,
              and give people real ownership of outcomes — from HNW portfolios to
              the platform itself.
            </p>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
            {VALUES.map(([t, b], i) => (
              <div key={t} className="reveal" style={{ borderTop: "1px solid var(--alabaster-dim)", paddingTop: 22 }}>
                <span style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", color: "var(--champagne)" }}>0{i + 1}</span>
                <h3 className="h-md" style={{ margin: "12px 0 10px" }}>{t}</h3>
                <p className="muted" style={{ fontSize: "0.95rem" }}>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active postings */}
      <section className="section dark" style={{ paddingTop: "clamp(56px,8vw,104px)" }}>
        <div className="shell">
          <div className="spaced reveal" style={{ marginBottom: 36, alignItems: "flex-end" }}>
            <div>
              <span className="eyebrow on-dark">Open positions</span>
              <h2 className="h-xl" style={{ marginTop: 16 }}>One seat. The right one.</h2>
            </div>
            <span className="badge"><span className="dot" /> 1 active role</span>
          </div>

          <div className="reveal" style={{ background: "var(--obsidian-soft)", border: "1px solid var(--obsidian-line)", borderRadius: 8, overflow: "hidden" }}>
            <button
              onClick={() => setOpen((o) => !o)}
              style={{ width: "100%", background: "none", border: "none", color: "var(--paper-ink)", textAlign: "left", padding: "clamp(24px,3vw,40px)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20 }}
            >
              <div>
                <div className="flexrow" style={{ gap: 10, marginBottom: 12 }}>
                  <span className="tag" style={{ borderColor: "var(--obsidian-line)", color: "var(--champagne)" }}>Internship</span>
                  <span className="tag" style={{ borderColor: "var(--obsidian-line)", color: "var(--paper-ink-soft)" }}>Strategy & Operations</span>
                  <span className="badge live"><span className="dot" /> Hiring now</span>
                </div>
                <h3 className="h-md">Management Trainee — Real Estate Operations & Strategy</h3>
              </div>
              <span style={{ fontSize: "1.8rem", color: "var(--champagne)" }}>{open ? "–" : "+"}</span>
            </button>

            {open && (
              <div style={{ padding: "0 clamp(24px,3vw,40px) clamp(28px,3vw,44px)" }}>
                <hr className="rule" style={{ marginBottom: 28 }} />
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "28px 48px" }}>
                  <Detail head="Eligibility" body="MBA graduates from the 2026 and 2027 passout batches. Final-year candidates graduating in those years may apply for the relevant cohort." />
                  <Detail head="Format" body="Full-time, in-house accelerator internship with a clear path to a full-time strategy seat for top performers." />
                  <Detail head="What you'll own" body="A high-impact accelerator across the engine of the business: property monetization, high-net-worth (HNW) portfolio management, and real estate arbitrage strategy." />
                  <Detail head="You'll thrive if" body="You're fluent in numbers, comfortable around capital and clients, and want to learn deal-making from the people actually closing the deals." />
                </div>

                <div style={{ marginTop: 30 }}>
                  <h4 style={{ fontFamily: "var(--sans)", fontSize: "0.74rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--champagne)", marginBottom: 14 }}>The accelerator covers</h4>
                  <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                    {["Property monetization & pricing strategy", "HNW portfolio management", "Real estate arbitrage & flip economics", "Direct bank-alliance & EMI structuring", "Title-vetting & risk operations", "Design-build feasibility & ROI modelling"].map((x) => (
                      <div key={x} style={{ borderLeft: "1px solid var(--champagne)", paddingLeft: 14, fontSize: "0.92rem" }} className="muted">{x}</div>
                    ))}
                  </div>
                </div>

                <div className="flexrow" style={{ marginTop: 36, gap: 16 }}>
                  <Link to="/contact" className="btn btn-gold">Apply for 2026 / 2027 Cohort <span className="arr">→</span></Link>
                  <span className="muted" style={{ fontSize: "0.85rem" }}>Rolling selection · limited seats per cohort</span>
                </div>
              </div>
            )}
          </div>

          <p className="muted reveal" style={{ marginTop: 28, fontSize: "0.9rem", maxWidth: 600 }}>
            Don't see your exact role but think you belong here? Write to us anyway
            — exceptional people make their own seat. <Link to="/contact" className="gold-text">Get in touch →</Link>
          </p>
        </div>
      </section>

      <style>{`
        @media (max-width: 820px) {
          .section .grid[style*="repeat(3"] { grid-template-columns: 1fr !important; }
          .grid[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

function Detail({ head, body }) {
  return (
    <div>
      <h4 style={{ fontFamily: "var(--sans)", fontSize: "0.74rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--champagne)", marginBottom: 8 }}>{head}</h4>
      <p className="muted" style={{ fontSize: "0.96rem" }}>{body}</p>
    </div>
  );
}
